/**
 * TemplateField (Field) Component
 *
 * Purpose: Renders a single form field by looking up its metadata in the field
 *          registry and delegating rendering to the appropriate FieldRenderer.
 *          When in Maestro mode (appliedFieldKeys provided), applies a border-color
 *          indicator and hover tooltip driven by Maestro's match confidence (ORCH-251),
 *          and associates the indicator status text with the input via aria-describedby
 *          so screen readers can announce it (ORCH-237).
 * Architecture: Reads field metadata from TemplateFormContext, registers with
 *               placed fields set and ancestor FieldGroups, then renders via
 *               getFieldRenderer() from metadata-form/FieldRenderers.
 *               In Maestro mode, wraps the renderer in FieldIndicatorWrapper which
 *               provides the visual Tooltip + border-color Box and the hidden
 *               accessible description element.
 * Dependencies: React, MUI (Box), TemplateFormContext (useTemplateFormContext),
 *               FieldGroupContext (useFieldGroups), metadata-form/FieldRenderers (getFieldRenderer),
 *               ./FieldIndicatorIcon (getFieldIndicatorState, valuePresent),
 *               ./FieldIndicatorWrapper, ./types (TemplateFieldProps)
 */

import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { useTemplateFormContext } from './TemplateFormContext';
import { useFieldGroups } from './FieldGroupContext';
import { getFieldRenderer } from '../metadata-form/FieldRenderers';
import { getFieldIndicatorState, valuePresent, INDICATOR_BORDER } from './FieldIndicatorIcon';
import FieldIndicatorWrapper, { indicatorDescriptorId } from './FieldIndicatorWrapper';
import type { TemplateFieldProps } from './types';
import { fieldSpanAllSx, spansAllColumns } from '../form-layout/fieldGrid';

/**
 * TemplateField — places a single metadata-driven field in a template layout
 *
 * Purpose: Renders one field by fieldDefId using its metadata-defined renderer
 * Logic: 1) Registers fieldDefId as "placed" so ExtraFields excludes it
 *        2) Registers with all ancestor FieldGroups for validation tracking
 *        3) Looks up ViewField from registry, applies label/required overrides
 *        4) Delegates rendering to getFieldRenderer(datatype)
 *        5) In Maestro mode, wraps in FieldIndicatorWrapper for border-color +
 *           accessible description; injects aria-describedby into the renderer's
 *           inputProps so screen readers associate the status text with the input
 * Input: TemplateFieldProps — id, labelOverride, requiredOverride, readOnly, fullWidth, componentProps
 * Output: Rendered field component or null if field not found in view config
 */
const TemplateField: React.FC<TemplateFieldProps> = ({
  id,
  labelOverride,
  requiredOverride,
  readOnly,
  fullWidth,
  span,
  componentProps,
}) => {
  const {
    fieldRegistry,
    markPlaced,
    values,
    errors,
    touched,
    actions,
    entityId,
    readOnly: formReadOnly,
    indicatorMode,
    appliedFieldKeys,
    fieldResults,
  } = useTemplateFormContext();
  const fieldGroups = useFieldGroups();

  // Register as placed and with ancestor FieldGroups
  useEffect(() => {
    markPlaced(id);
    fieldGroups.forEach((group) => group.register(id));
    return () => {
      fieldGroups.forEach((group) => group.unregister(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const viewField = fieldRegistry.get(id);
  if (!viewField) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[TemplateField] Field "${id}" not found in view config`);
    }
    return null;
  }

  // Apply overrides
  const field = {
    ...viewField,
    ...(labelOverride !== undefined && { label_override: labelOverride }),
    ...(requiredOverride !== undefined && { is_required: requiredOverride }),
  };

  const Renderer = getFieldRenderer(field.field_definition.datatype);
  const isRequired = field.is_required ?? false;

  // Maestro mode: appliedFieldKeys is provided (even as an empty Set).
  const isMaestroMode = appliedFieldKeys !== undefined;

  const indicator = isMaestroMode
    ? getFieldIndicatorState({
        fieldId: id,
        isRequired,
        indicatorMode,
        appliedFieldKeys,
        fieldResults,
        hasValue: valuePresent(values[id]),
      })
    : { state: null, tooltipText: null };

  // Extract caller-supplied inputProps from componentProps so we can merge
  // aria-describedby without clobbering any caller-supplied native input attrs.
  const { inputProps: callerInputProps, ...restComponentProps } = componentProps || {};

  const mergedInputProps = isMaestroMode
    ? { 'aria-describedby': indicatorDescriptorId(id), ...(callerInputProps || {}) }
    : callerInputProps || undefined;

  const borderColor = indicator.state ? INDICATOR_BORDER[indicator.state] : undefined;

  const rendererEl = (
    <Renderer
      field={field}
      value={values[id]}
      onChange={(value: any) => actions.setValue(id, value)}
      error={errors[id]}
      touched={touched[id]}
      disabled={readOnly || formReadOnly}
      entityId={entityId}
      indicatorBorderColor={borderColor}
      {...restComponentProps}
      {...(mergedInputProps !== undefined ? { inputProps: mergedInputProps } : {})}
    />
  );

  return (
    <Box
      sx={{
        ...(fullWidth || span === 'all' || spansAllColumns(field.field_definition)
          ? fieldSpanAllSx
          : {}),
        minWidth: 0,
      }}
    >
      {isMaestroMode ? (
        <FieldIndicatorWrapper fieldId={id} indicator={indicator}>
          {rendererEl}
        </FieldIndicatorWrapper>
      ) : (
        rendererEl
      )}
    </Box>
  );
};

export default TemplateField;
